import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  size as floatingSize,
  offset,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import { faSearch, faTimes, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faAngleDown, faAngleUp, faCheck, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { LinearProgress } from '@material-ui/core';
import Fuse from 'fuse.js';
import { chain } from 'lodash';
import * as React from 'react';
import {
  ChangeEvent,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { zIndexVoxel } from '../../../logic/zIndexVoxel';
import VoxelButton from '../Buttons/VoxelButton';
import VoxelIconButton from '../Buttons/VoxelIconButton';
import VoxelFormsLabel from './VoxelFormsLabel';
import VoxelSelectItem from './VoxelSelectItem';

export interface GenericOption {
  value: any;
  title: string;
  alternativeTitle?: string;
  subtitle?: string;
  disabled?: boolean;
  icon?: ReactElement<FontAwesomeIconProps>;
}

export interface VoxelAutocompleteProps {
  id?: string;
  label?: string;
  labelPosition?: 'top' | 'left';
  size?: 'sm' | 'md';
  preText?: ReactNode;
  selectedOptionValues?: any[];
  multi?: boolean;
  setSelectedOptions: (options: GenericOption[]) => void;
  options: GenericOption[];
  placeholder?: string;
  disabled?: boolean;
  fixedOptions?: ReactNode;
  freeAdd?: boolean; //Allow users write their own option
  helperText?: string;
  showHelperText?: boolean;
  outlined?: boolean;
  autoFocus?: boolean;
  inputWidthClass?: string;
  searchPlaceholder?: string;
  emptyAllowed?: boolean;
  inputBg?: string;
  backendSearchTerm?: string;
  setBackendSearchTerm?: Dispatch<SetStateAction<string>>;
  infoText?: ReactElement;
  hasLock?: string;
  loadingOptions?: boolean;
  nrVisibleMultiOptions?: number;
  maxHeightOptions?: number;
  inputClassNames?: string;
  tabIndex?: number;
  hideSearch?: boolean;
  isRequired?: boolean;
  bottomElements?: ReactElement[];
}

const VoxelAutocomplete: React.FC<VoxelAutocompleteProps> = ({
  id,
  selectedOptionValues,
  multi,
  setSelectedOptions,
  disabled,
  label,
  labelPosition = 'left',
  size = 'md',
  placeholder,
  options,
  fixedOptions,
  freeAdd = false,
  preText,
  helperText,
  showHelperText,
  outlined = true,
  autoFocus = false,
  inputWidthClass = 'w-full',
  emptyAllowed = true,
  inputBg = 'bg-white',
  backendSearchTerm,
  setBackendSearchTerm,
  infoText,
  hasLock: editDisabledText,
  loadingOptions,
  searchPlaceholder,
  nrVisibleMultiOptions = 1,
  maxHeightOptions = 300,
  inputClassNames,
  tabIndex,
  hideSearch,
  isRequired,
  bottomElements,
}) => {
  // Floating UI
  const [open, setOpen] = useState<boolean>(autoFocus);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const { refs, floatingStyles, context } = useFloating<HTMLInputElement>({
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange: setOpen,
    middleware: [
      offset(8),
      flip({ padding: 10 }),
      floatingSize({
        apply({ rects, availableHeight, elements }: any) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width + 2}px`,
            maxHeight: `${maxHeightOptions}px`,
          });
        },
        padding: 10,
      }),
    ],
  });

  const role = useRole(context, { role: 'listbox' });
  const dismiss = useDismiss(context, {
    outsidePress: (event: any) => !event.target.closest('.toast'),
    escapeKey: true,
  });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true,
  });

  const focus = useFocus(context, { enabled: !open });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    role,
    dismiss,
    listNav,
    focus,
  ]);

  // Create options
  const selectedOptions: GenericOption[] = useMemo(() => {
    let chainedValue = chain(selectedOptionValues)
      .map((selectedValue) => {
        const matchingOption = options.find((opt) => opt.value === selectedValue);
        if (!matchingOption && freeAdd) return { value: selectedValue, title: selectedValue };
        if (matchingOption) return matchingOption;
        return null;
      })
      .filter((opt) => !!opt);
    if (!backendSearchTerm) {
      chainedValue = chainedValue.orderBy([(v) => v.title, (v) => v.value]);
    }
    return chainedValue.value();
  }, [selectedOptionValues, options, freeAdd]);

  const selectedOptionsDictionary = useMemo(() => {
    const dictionary: Record<any, GenericOption> = {};
    selectedOptions.forEach((opt) => (dictionary[opt.value] = opt));
    return dictionary;
  }, [selectedOptions]);

  let defaultSearchTerm = '';

  // Search field logic
  const [localSearchTerm, setLocalSearchTerm] = useState<string>(
    backendSearchTerm ?? defaultSearchTerm
  );

  // Make sure search state stays in sync with data
  useEffect(() => setLocalSearchTerm(defaultSearchTerm), [selectedOptions.length]);
  useEffect(() => {
    if (setBackendSearchTerm) {
      setBackendSearchTerm(localSearchTerm);
    }
  }, [localSearchTerm]);

  // Filter options
  const fuse = useMemo(() => new Fuse(options ?? [], { keys: ['title'] }), [options]);
  const fuseOptions = useMemo(() => {
    if (!!backendSearchTerm) {
      return options;
    }
    return !localSearchTerm?.length
      ? options
      : fuse.search(localSearchTerm).map((result) => result.item);
  }, [fuse, localSearchTerm, backendSearchTerm]);

  // Scroll up oupon filtering
  useEffect(() => {
    if (open) {
      setActiveIndex(0);
    }
  }, [fuseOptions]);

  // Free add
  const showFreeAddButton =
    freeAdd &&
    !!localSearchTerm.length &&
    open &&
    fuseOptions.every((opt) => opt.title.toLowerCase() !== localSearchTerm.toLowerCase());

  // Select option
  const handleSelectOption = (
    selectedOption?: GenericOption,
    close: boolean = true,
    toggle: boolean = true
  ) => {
    // If exists in values - remove
    if (
      emptyAllowed &&
      toggle &&
      selectedOption &&
      selectedOptions.some((opt) => opt.value === selectedOption?.value)
    ) {
      setSelectedOptions(selectedOptions.filter((opt) => opt.value !== selectedOption.value));
    }

    // If not exists in values
    else if (selectedOption && multi) {
      setSelectedOptions([...selectedOptions, selectedOption]);
    } else if (selectedOption && !multi) {
      setSelectedOptions([selectedOption]);
    } else if (emptyAllowed && !selectedOption) {
      setSelectedOptions([]);
    }

    close && setOpen(false);
    setLocalSearchTerm(defaultSearchTerm);
  };

  const handleSelectOptionOnName = useCallback(
    (name?: string, close: boolean = true) => {
      // Default behaviour - just select what's available
      const selectedOption = options.find((opt) => opt.title.toLowerCase() === name?.toLowerCase());
      if (!freeAdd || selectedOption) {
        handleSelectOption(selectedOption.value, close, false);
      }

      // If free add
      if (freeAdd && !!name?.length && !selectedOption) {
        handleSelectOption({ title: name, value: name }, close, false);
      }

      close && setOpen(false);
    },
    [options, freeAdd]
  );

  const hasSubtitles = useMemo(() => options.some((o) => !!o.subtitle), [options]);

  // Styling
  let labelPositionCSS = '';
  if (labelPosition === 'left') {
    labelPositionCSS = 'flex-row flex-nowrap items-center gap-x-2';
  }
  if (labelPosition === 'top') {
    labelPositionCSS = 'flex-col gap-y-2';
  }

  const bottomElementsHeight = !!bottomElements?.length ? bottomElements?.length * 34.16 : 0;
  const searchHeight = 40.02;
  const fuseHeight = !!fuseOptions.length ? 33.87 : 0;
  const maxHeightScrollContainer =
    maxHeightOptions - searchHeight - fuseHeight - bottomElementsHeight;

  const handleSearchInputKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.stopPropagation();
      event.preventDefault();
      // Tabbing logic
      const option = fuseOptions[activeIndex];
      if (!!option) {
        handleSelectOption(option, !multi);
        refs.domReference.current.focus();
        return;
      }

      // Free add
      if (showFreeAddButton) {
        handleSelectOptionOnName(localSearchTerm);
        return;
      }

      // Enter means close if there's no option
      if (!option) {
        setOpen(false);

        return;
      }
    }
  };

  return (
    <div className={`flex ${labelPositionCSS} relative ${inputWidthClass}`}>
      <VoxelFormsLabel
        id={id}
        label={label}
        disabled={disabled}
        hasLock={!!editDisabledText?.length}
        infoText={infoText}
      />
      <div className={`relative group flex flex-row items-center flex-nowrap flex-grow`}>
        {preText !== null && <div>{preText}</div>}
        <div className={`flex flex-col flex-grow ${inputWidthClass}`}>
          {/* Hidden input element just so that this works with tabbing through a form */}
          {/* Also the width is applied more correctly here */}
          <input
            className={`h-0`}
            disabled={disabled}
            id={id}
            autoFocus={autoFocus}
            readOnly
            tabIndex={tabIndex}
            {...getReferenceProps({
              ref: refs.setReference,
              tabIndex: tabIndex,
              autoFocus: autoFocus,
              disabled: disabled,
              readOnly: true,
            })}
          />
          {/* What appears to be the main input field (actually a div) */}
          <div
            className={`
            rounded-md toast ${inputBg} ${inputClassNames}
            overflow-hidden
            flex-grow
            ${size === 'md' ? 'h-9' : 'h-8'}
            ${size === 'md' ? 'py-2' : 'py-1.5'} px-2.5
            outline-none
            ${outlined && !showHelperText && !open && 'ring-1'}
            ${!disabled && 'cursor-pointer'}

            ${!disabled && 'placeholder-voxel-gray-400'}
            ${outlined && !disabled && !showHelperText && !open && 'ring-voxel-gray-300'}
            ${showHelperText && !disabled && 'ring-red-500 ring-2'} 
            ${!disabled && !open && 'hover:ring-voxel-gray-400 hover:ring-1'}
            ${!disabled && open && 'ring-2 ring-primary-600'}

            ${!disabled && 'focus:ring-2 focus:ring-primary-600'}

            ${disabled && 'placeholder-voxel-gray-300'}
            ${disabled && 'ring-1 ring-voxel-gray-200 text-gray-500'}
            
            ${!!editDisabledText?.length && 'ring-voxel-gray-200 text-gray-500'}
            flex flex-row items-center gap-2 
            
        `}
            onClick={() => setOpen(!open)}
            ref={refs.setPositionReference}
          >
            {/* Visible options */}
            {!selectedOptions.length && !!placeholder?.length && (
              <span className="text-voxel-gray-400">{placeholder}</span>
            )}
            {!multi &&
              selectedOptions.map((opt, index) => (
                <span key={index} className="flex flex-row items-start gap-x-2">
                  {opt.icon}
                  {opt.alternativeTitle ? opt.alternativeTitle : opt.title}
                </span>
              ))}
            {multi &&
              selectedOptions.map((opt, index) => {
                if (!open && index > nrVisibleMultiOptions - 1) return null;
                return (
                  <span
                    className={`
                    rounded-full
                    h-5 pl-2.5 ${!(open && !disabled) && 'pr-2.5'}
                    text-xs text-voxel-gray-800
                    bg-voxel-gray-100
                    flex-nowrap flex flex-row items-center
                    whitespace-nowrap
                  `}
                    key={index}
                  >
                    {opt.title}
                    {open && !disabled && (
                      <VoxelIconButton
                        size="xxs"
                        icon={<FontAwesomeIcon icon={faTimes} size="sm" />}
                        color="white"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSelectOption(opt, false);
                        }}
                      />
                    )}
                  </span>
                );
              })}
            {multi && !open && selectedOptions.length > nrVisibleMultiOptions && (
              <span
                className={`
                    rounded-full
                    h-5 pl-2.5 pr-2.5
                    text-xs text-voxel-gray-800
                    bg-voxel-gray-100
                    flex-nowrap flex flex-row items-center
                  `}
              >
                +{selectedOptions.length - nrVisibleMultiOptions}
              </span>
            )}
          </div>
        </div>
        {!disabled && !multi && (
          <div
            className={`absolute z-2 right-0.5 ${size === 'md' ? 'top-1.5 ' : 'top-1'}
          flex flex-row items-center gap-0.5
          `}
          >
            {!!selectedOptions.length && emptyAllowed && (
              <VoxelIconButton
                color="white"
                size="xs"
                className="toast"
                icon={<FontAwesomeIcon icon={faXmark} />}
                onClick={() => handleSelectOption(undefined)}
              />
            )}
            <VoxelIconButton
              size="xs"
              color="white"
              className="toast"
              icon={<FontAwesomeIcon icon={open ? faAngleUp : faAngleDown} />}
              onClick={(e) => {
                setOpen((prev) => !prev);
                e.stopPropagation();
              }}
            />
          </div>
        )}
        <FloatingPortal>
          {open && !disabled && (
            <FloatingFocusManager context={context} initialFocus={-1} visuallyHiddenDismiss>
              <div
                {...getFloatingProps({
                  ref: refs.setFloating,
                  style: floatingStyles,
                  className: `
                    box-border
                    shadow-lg bg-white
                    rounded
                    ring-1 ring-voxel-gray-200 ${zIndexVoxel.selectContainer}
                  `,
                })}
              >
                {/* This is NOT the real search element - it's just to enable tabbing/enter etc. in case search isn't enabled */}
                {hideSearch && (
                  <input
                    tabIndex={-1}
                    className="absolute opacity-0 w-0 h-0"
                    autoFocus
                    onKeyDown={handleSearchInputKeyUp}
                  />
                )}
                {!hideSearch && (
                  <div
                    className={`
                    rounded-t-md
                    py-2.5 px-3
                    bg-white
                    flex items-center flex-row gap-3
                `}
                  >
                    <FontAwesomeIcon icon={faSearch} className="text-voxel-gray-500" />
                    <input
                      id={id + '-search'}
                      autoComplete="off"
                      spellCheck="false"
                      autoCapitalize="off"
                      autoCorrect="off"
                      autoFocus
                      placeholder={searchPlaceholder ?? 'Search'}
                      tabIndex={-1}
                      value={localSearchTerm ?? ''}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setLocalSearchTerm(event.target.value);
                      }}
                      onKeyDown={handleSearchInputKeyUp}
                      className={`
                bg-white
                text-voxel-gray-800
                placeholder-voxel-gray-400
                w-full
                rounded-t-md
                focus:outline-none
              `}
                    />
                    {showFreeAddButton && (
                      <VoxelButton
                        color="success"
                        title="Add"
                        size="xs"
                        startIcon={<FontAwesomeIcon icon={faPlus} size="xs" />}
                        onClick={() => handleSelectOptionOnName(localSearchTerm)}
                        className="-my-2"
                      />
                    )}
                  </div>
                )}
                <div className="w-full border-b border-voxel-gray-200" />
                {fixedOptions}
                <div
                  className={`
                    overflow-y-auto scroll
                    select-none
                    p-1.5
                    flex flex-col
                  `}
                  style={{ maxHeight: maxHeightScrollContainer }}
                >
                  {loadingOptions && (
                    <div className="w-full absolute opacity-30 h-0.5 -ml-1.5 -mt-1.5 overflow-y-hidden">
                      <LinearProgress />
                    </div>
                  )}
                  {fuseOptions.map((item, index) => {
                    const selected = !!selectedOptionsDictionary[item?.value];
                    return (
                      <VoxelSelectItem
                        {...getItemProps({
                          key: item.value + '-' + index,
                          ref(node) {
                            listRef.current[index] = node;
                          },
                          onClick() {
                            handleSelectOption(item, !multi);
                            refs.domReference.current?.focus();
                          },
                          className: 'flex flex-row items-center gap-x-1',
                        })}
                        active={activeIndex === index}
                        selected={!!selectedOptionsDictionary[item?.value]}
                      >
                        <div className="flex flex-col">
                          <div className="flex flex-row items-center gap-x-2">
                            {item.icon}
                            <span className={hasSubtitles ? 'font-medium' : ''}>{item.title}</span>
                          </div>
                          {hasSubtitles && (
                            <div className="text-gray-500 text-[13px]">{item.subtitle}</div>
                          )}
                        </div>
                        <span className="flex-grow" />
                        {selected && (
                          <FontAwesomeIcon icon={faCheck} className="ml-1 text-voxel-gray-400" />
                        )}
                      </VoxelSelectItem>
                    );
                  })}
                </div>
                {!!fuseOptions.length && !hideSearch && (
                  <div className="px-4 py-2 flex items-center gap-2 border-t border-voxel-gray-200 bg-gray-50 rounded-b-md">
                    <span className="px-1 py-0.5 text-[9px] font-mono text-voxel-gray-500 rounded-sm ring-1 ring-voxel-gray-300 bg-gray-100">
                      &darr;
                    </span>
                    <span className="px-1 py-0.5 text-[9px] font-mono text-voxel-gray-500 rounded-sm ring-1 ring-voxel-gray-300 bg-gray-100">
                      &uarr;
                    </span>
                    <span className="px-1 py-0.5 text-[9px] font-mono text-voxel-gray-500 rounded-sm ring-1 ring-voxel-gray-300 bg-gray-100">
                      ENTER
                    </span>
                  </div>
                )}
                {bottomElements?.map((be) => {
                  return (
                    <div className="px-4 py-2 flex items-center gap-2 border-t border-voxel-gray-200 bg-gray-50 last:rounded-b-md">
                      {be}
                    </div>
                  );
                })}
              </div>
            </FloatingFocusManager>
          )}
        </FloatingPortal>
      </div>
      {showHelperText ? (
        <p className="absolute -bottom-6 left-0.5 text-xs text-red-500">{helperText}</p>
      ) : undefined}
    </div>
  );
};

export default VoxelAutocomplete;
